/**
 * Models Tree Panel
 *
 * A draggable, toggleable panel that lists every loaded fragment / GLTF model
 * as a tree:
 *
 *   📁 <Folder name>  (N models)
 *     ├─ 👁  ▷  Model A
 *     ├─ 👁  ▷  Model B
 *     └─ ...
 *
 * For every model row:
 *   - 👁  show / hide toggle (sets model.object.visible)
 *   - ▷   "fit to view" — moves the camera so the whole model fills the frame
 *   - label shows the model name (truncated)
 *
 * Style matches the Clipper / Element Info / Model Info panels exactly:
 * fixed-position, cyan-glow border, draggable header, × close button.
 *
 * Public API:
 *   const handle = createModelsTreePanel({ fragments, world, folderName, refreshFragments });
 *   handle.togglePanel();
 *   handle.refresh(); // call this whenever model set changes
 *   handle.dispose();
 */

import * as THREE from "three";
import * as FRAGS from "@thatopen/fragments";

interface ModelsTreePanelOptions {
  fragments: FRAGS.FragmentsModels;
  world: any; // OBC.World
  folderName?: string;
  /**
   * Optional lookup of friendly model names by modelId. The
   * @thatopen/fragments lib doesn't store the original file name on the
   * FragmentsModel, so we need the caller (which knows the URL params /
   * model metadata) to provide it. Falls back to modelId when missing.
   */
  modelNames?: Map<string, string> | Record<string, string>;
  /**
   * Extra non-FRAGS roots to display in the tree (e.g. GLTF/GLB models
   * loaded directly via three's GLTFLoader). The map key is the same
   * modelId used in the URL `models=` payload so visibility, fit-to-view,
   * the transform gizmo and share state stay consistent with FragmentsModels
   * entries.
   */
  extraRoots?: Map<string, THREE.Object3D> | Record<string, THREE.Object3D>;
  /** Called whenever visibility changes so the renderer updates immediately. */
  refreshFragments?: () => void;
  /**
   * Optional callback fired when the user clicks the move (XYZ gizmo)
   * icon for a model. The viewer wires this to a TransformControls
   * gizmo so the model can be repositioned manually inside the viewer
   * only — never affecting admin DB or the main app placement.
   *
   * The callback should toggle (attach/detach) on each call.
   */
  onMoveClick?: (modelId: string, object: THREE.Object3D, displayName: string) => void;
  /** Resolver — return true if the gizmo is currently attached to this model. */
  isMoveActive?: (modelId: string) => boolean;
}

export interface ModelsTreePanelHandle {
  panel: HTMLElement;
  togglePanel(): void;
  setPanelVisible(visible: boolean): void;
  isPanelVisible(): boolean;
  refresh(): void;
  dispose(): void;
}

export function createModelsTreePanel(opts: ModelsTreePanelOptions): ModelsTreePanelHandle {
  const { fragments, world, folderName, refreshFragments } = opts;

  // Normalize extraRoots into a Map for uniform iteration
  const extraRootsMap: Map<string, THREE.Object3D> = (() => {
    const er = opts.extraRoots;
    if (!er) return new Map();
    if (er instanceof Map) return er;
    return new Map(Object.entries(er));
  })();

  // Normalize modelNames into a Map for uniform lookup
  const nameMap = new Map<string, string>();
  if (opts.modelNames) {
    if (opts.modelNames instanceof Map) {
      for (const [k, v] of opts.modelNames) nameMap.set(k, v);
    } else {
      for (const [k, v] of Object.entries(opts.modelNames)) nameMap.set(k, v as string);
    }
  }
  const lookupName = (modelId: string, model: any): string => {
    return (
      nameMap.get(modelId) ||
      model?.name ||
      model?.fileName ||
      model?.modelId ||
      modelId
    );
  };

  // ---------- Panel shell ----------
  const panel = document.createElement("div");
  panel.id = "models-tree-panel";
  panel.style.cssText = `
    position: fixed;
    top: 80px;
    right: 12px;
    width: 16rem;
    max-height: 70vh;
    z-index: 9999;
    background: rgba(10, 14, 39, 0.95);
    border: 1px solid #44d3de;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(68, 211, 222, 0.3);
    backdrop-filter: blur(10px);
    color: #e0e0e0;
    font: 12px -apple-system, 'Segoe UI', Helvetica, sans-serif;
    pointer-events: auto;
    user-select: none;
    overflow: hidden;
    display: none;
    flex-direction: column;
  `;

  // Forward declaration for the close button
  let setPanelVisible: (visible: boolean) => void = () => {};

  // ---------- Header ----------
  const header = document.createElement("div");
  header.setAttribute("data-panel-header", "true");
  header.style.cssText = `
    padding: 0.5rem;
    border-bottom: 1px solid rgba(68, 211, 222, 0.3);
    color: #44d3de;
    font-weight: 600;
    font-size: 0.75rem;
    cursor: move;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;
  const titleSpan = document.createElement("span");
  titleSpan.textContent = "Models";
  header.appendChild(titleSpan);
  const closeBtn = document.createElement("button");
  closeBtn.innerHTML = "&times;";
  closeBtn.title = "Close panel";
  closeBtn.style.cssText = `
    background: transparent; border: none; color: #44d3de;
    cursor: pointer; font-size: 16px; line-height: 1;
    padding: 0 4px; border-radius: 4px;
  `;
  closeBtn.onmouseenter = () => { closeBtn.style.background = "rgba(255, 255, 255, 0.12)"; };
  closeBtn.onmouseleave = () => { closeBtn.style.background = "transparent"; };
  closeBtn.addEventListener("click", (e) => { e.stopPropagation(); setPanelVisible(false); });
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // ---------- Body ----------
  const body = document.createElement("div");
  body.style.cssText = `
    flex: 1;
    overflow: auto;
    padding: 0.5rem;
    font-size: 0.7rem;
    color: #e0e0e0;
  `;
  panel.appendChild(body);

  // Folder root
  const folderRow = document.createElement("div");
  folderRow.style.cssText = `
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    color: #44d3de;
    font-weight: 600;
    cursor: pointer;
    border-radius: 4px;
  `;
  folderRow.onmouseenter = () => { folderRow.style.background = "rgba(68, 211, 222, 0.08)"; };
  folderRow.onmouseleave = () => { folderRow.style.background = "transparent"; };

  const chevron = document.createElement("span");
  chevron.textContent = "▼";
  chevron.style.cssText = "display: inline-block; width: 10px; font-size: 9px; color: #44d3de;";

  const folderIcon = document.createElement("span");
  folderIcon.innerHTML = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>`;
  folderIcon.style.cssText = "display: inline-flex; align-items: center; color: #44d3de;";

  const folderLabel = document.createElement("span");
  folderLabel.style.cssText = "flex: 1; color: #44d3de; font-size: 11px;";
  folderLabel.textContent = folderName || "Models";

  const folderCount = document.createElement("span");
  folderCount.style.cssText = "color: #8db5b9; font-size: 10.5px; font-weight: normal;";
  folderCount.textContent = "0";

  folderRow.appendChild(chevron);
  folderRow.appendChild(folderIcon);
  folderRow.appendChild(folderLabel);
  folderRow.appendChild(folderCount);
  body.appendChild(folderRow);

  // Children container (indented)
  const childList = document.createElement("div");
  childList.style.cssText = `
    margin-left: 18px;
    padding-left: 8px;
    border-left: 1px dashed rgba(68, 211, 222, 0.25);
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 2px;
  `;
  body.appendChild(childList);

  let collapsed = false;
  folderRow.addEventListener("click", () => {
    collapsed = !collapsed;
    chevron.textContent = collapsed ? "▶" : "▼";
    childList.style.display = collapsed ? "none" : "flex";
  });

  // Empty hint
  const emptyHint = document.createElement("div");
  emptyHint.style.cssText = "color: #688080; font-style: italic; padding: 6px 0; font-size: 10.5px;";
  emptyHint.textContent = "No models loaded";
  childList.appendChild(emptyHint);

  // ---------- Per-model row ----------
  const buildModelRow = (modelId: string, model: any) => {
    const row = document.createElement("div");
    row.dataset.modelId = modelId;
    row.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 3px 4px;
      border-radius: 3px;
      transition: background 100ms ease;
    `;
    row.onmouseenter = () => { row.style.background = "rgba(68, 211, 222, 0.06)"; };
    row.onmouseleave = () => { row.style.background = "transparent"; };

    const obj: THREE.Object3D | undefined = model.object;
    const isVisible = obj ? obj.visible !== false : true;

    // Show / hide toggle (eye icon)
    const eyeBtn = document.createElement("button");
    eyeBtn.title = "Show / hide";
    const eyeOn = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>`;
    const eyeOff = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>`;
    let visible = isVisible;
    eyeBtn.innerHTML = visible ? eyeOn : eyeOff;
    eyeBtn.style.cssText = `
      background: transparent; border: none; cursor: pointer;
      color: ${visible ? "#44d3de" : "#688080"};
      padding: 2px; border-radius: 3px;
      display: flex; align-items: center; flex-shrink: 0;
    `;
    eyeBtn.onmouseenter = () => { eyeBtn.style.background = "rgba(68, 211, 222, 0.18)"; };
    eyeBtn.onmouseleave = () => { eyeBtn.style.background = "transparent"; };
    eyeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      visible = !visible;
      try {
        if (obj) obj.visible = visible;
      } catch (err) {
        console.warn("[modelsTree] set visible failed", err);
      }
      eyeBtn.innerHTML = visible ? eyeOn : eyeOff;
      eyeBtn.style.color = visible ? "#44d3de" : "#688080";
      label.style.opacity = visible ? "1" : "0.5";
      try { refreshFragments?.(); } catch { /* noop */ }
    });

    // Move (XYZ gizmo) toggle — viewer-only manual placement
    const moveBtn = document.createElement("button");
    moveBtn.title = "Move / rotate this model in the viewer (does not affect app)";
    moveBtn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="5 9 2 12 5 15"/>
        <polyline points="9 5 12 2 15 5"/>
        <polyline points="15 19 12 22 9 19"/>
        <polyline points="19 9 22 12 19 15"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <line x1="12" y1="2" x2="12" y2="22"/>
      </svg>`;
    const paintMoveBtn = () => {
      const active = !!opts.isMoveActive?.(modelId);
      moveBtn.style.cssText = `
        background: ${active ? "rgba(68, 211, 222, 0.55)" : "transparent"};
        border: none;
        color: ${active ? "#0a0e27" : "#44d3de"};
        cursor: pointer;
        padding: 2px;
        border-radius: 3px;
        display: flex;
        align-items: center;
        flex-shrink: 0;
      `;
    };
    paintMoveBtn();
    moveBtn.onmouseenter = () => {
      if (!opts.isMoveActive?.(modelId)) {
        moveBtn.style.background = "rgba(68, 211, 222, 0.18)";
      }
    };
    moveBtn.onmouseleave = () => { paintMoveBtn(); };
    moveBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!obj || !opts.onMoveClick) return;
      const name = lookupName(modelId, model);
      opts.onMoveClick(modelId, obj, name);
      // Repaint after toggle (viewer flips isMoveActive synchronously).
      setTimeout(paintMoveBtn, 0);
    });

    // Fit-to-view (arrow icon)
    const fitBtn = document.createElement("button");
    fitBtn.title = "Fit camera to this model";
    fitBtn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 3 21 3 21 9"/>
        <polyline points="9 21 3 21 3 15"/>
        <line x1="21" y1="3" x2="14" y2="10"/>
        <line x1="3" y1="21" x2="10" y2="14"/>
      </svg>`;
    fitBtn.style.cssText = `
      background: transparent; border: none; color: #44d3de;
      cursor: pointer; padding: 2px; border-radius: 3px;
      display: flex; align-items: center; flex-shrink: 0;
    `;
    fitBtn.onmouseenter = () => { fitBtn.style.background = "rgba(68, 211, 222, 0.18)"; };
    fitBtn.onmouseleave = () => { fitBtn.style.background = "transparent"; };
    fitBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      try {
        if (!obj) return;
        const box = new THREE.Box3().setFromObject(obj);
        if (box.isEmpty()) {
          console.warn("[modelsTree] empty bounding box for", modelId);
          return;
        }
        const sphere = box.getBoundingSphere(new THREE.Sphere());
        const controls: any = world.camera.controls;
        if (typeof controls.fitToSphere === "function") {
          controls.fitToSphere(sphere, true);
        } else {
          // Fallback: move camera to 2.5× radius along current direction
          const target = sphere.center.clone();
          const camPos = new THREE.Vector3();
          controls.getPosition(camPos);
          const dir = new THREE.Vector3().subVectors(camPos, target);
          if (dir.lengthSq() < 1e-6) dir.set(1, 1, 1);
          dir.normalize().multiplyScalar(sphere.radius * 2.5);
          const np = target.clone().add(dir);
          controls.setLookAt(np.x, np.y, np.z, target.x, target.y, target.z, true);
        }
      } catch (err) {
        console.warn("[modelsTree] fit failed", err);
      }
    });

    // Label (model name) — truncate long names with ellipsis
    const label = document.createElement("span");
    const name = lookupName(modelId, model);
    label.textContent = name;
    label.title = name;
    label.style.cssText = `
      flex: 1;
      color: #cfeef0;
      font-size: 10.5px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      opacity: ${visible ? "1" : "0.5"};
    `;

    row.appendChild(eyeBtn);
    row.appendChild(moveBtn);
    row.appendChild(fitBtn);
    row.appendChild(label);
    return row;
  };

  // ---------- Refresh ----------
  const refresh = () => {
    childList.innerHTML = "";
    const list = fragments.models.list;
    const total = list.size + extraRootsMap.size;
    folderCount.textContent = String(total);
    if (total === 0) {
      childList.appendChild(emptyHint);
      return;
    }
    for (const [modelId, model] of list) {
      childList.appendChild(buildModelRow(modelId, model));
    }
    // GLTF / non-FRAGS roots — wrap as { object } so buildModelRow can
    // reuse the same visibility / fit-to-view / move logic without branching.
    for (const [modelId, root] of extraRootsMap) {
      childList.appendChild(buildModelRow(modelId, { object: root }));
    }
  };
  refresh();

  // Auto-refresh when models are added (fragments lib uses onItemSet)
  const onModelAdded = () => refresh();
  const onModelDeleted = () => refresh();
  try {
    fragments.models.list.onItemSet.add(onModelAdded);
    if ((fragments.models.list as any).onItemDeleted) {
      (fragments.models.list as any).onItemDeleted.add(onModelDeleted);
    }
  } catch { /* noop */ }

  // ---------- Drag handle ----------
  let dragging = false;
  let dragOffsetX = 0;
  let dragOffsetY = 0;

  header.addEventListener("mousedown", (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target === closeBtn || closeBtn.contains(target)) return;
    const rect = panel.getBoundingClientRect();
    dragOffsetX = e.clientX - rect.left;
    dragOffsetY = e.clientY - rect.top;
    dragging = true;
    header.style.cursor = "grabbing";
    e.preventDefault();
    e.stopPropagation();
  });
  const onPanelDrag = (e: MouseEvent) => {
    if (!dragging) return;
    e.preventDefault();
    const w = panel.offsetWidth;
    const maxX = window.innerWidth - 40;
    const maxY = window.innerHeight - 40;
    let nx = e.clientX - dragOffsetX;
    let ny = e.clientY - dragOffsetY;
    nx = Math.max(0 - w + 40, Math.min(nx, maxX));
    ny = Math.max(0, Math.min(ny, maxY));
    panel.style.left = `${nx}px`;
    panel.style.top = `${ny}px`;
    panel.style.right = "auto";
    panel.style.bottom = "auto";
  };
  const onPanelDragEnd = () => {
    if (dragging) {
      dragging = false;
      header.style.cursor = "move";
    }
  };
  document.addEventListener("mousemove", onPanelDrag);
  document.addEventListener("mouseup", onPanelDragEnd);

  document.body.appendChild(panel);

  // ---------- Toggle helpers ----------
  const isPanelVisible = () => panel.style.display !== "none";
  setPanelVisible = (visible: boolean) => {
    panel.style.display = visible ? "flex" : "none";
    if (visible) refresh();
  };
  const togglePanel = () => setPanelVisible(!isPanelVisible());

  return {
    panel,
    togglePanel,
    setPanelVisible,
    isPanelVisible,
    refresh,
    dispose() {
      document.removeEventListener("mousemove", onPanelDrag);
      document.removeEventListener("mouseup", onPanelDragEnd);
      try {
        fragments.models.list.onItemSet.remove(onModelAdded);
        if ((fragments.models.list as any).onItemDeleted) {
          (fragments.models.list as any).onItemDeleted.remove(onModelDeleted);
        }
      } catch { /* noop */ }
      panel.remove();
    },
  };
}
